import React, { useId, useRef, useState } from 'react';
import { compressImage, MAX_PHOTO_BYTES } from './imageUtils';
import { uploadPhoto } from './photoStorage';
import FormHeaderBanner from './FormHeaderBanner';
import { HEADER_POSITIONS, MAX_HEADER_TEXT, hasHeaderContent } from './formHeader';
import { ACTION_BTN, DANGER_ICON_BTN, LABEL } from './ui';

const ChevronIcon = ({ expanded }) => (
  <svg
    className={`w-4 h-4 shrink-0 text-purple-300 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// What the folded panel says is in the header, so it never has to be opened
// just to check.
const headerSummary = (header) => {
  const hasText = !!header.text.trim();
  if (hasText && header.logo) return 'Text and logo';
  if (hasText) return 'Text only';
  if (header.logo) return 'Logo only';
  return 'Not set';
};

// Builder panel for the optional report header: free text, an optional logo,
// and which side of the header that logo sits on. Everything here is optional —
// a form with an empty header simply renders no banner.
//
// It starts folded: a header is set up once and then left alone, while the
// questions below it are what an author comes back to edit. Folded, the title
// row still says what the header holds.
//
// The logo is uploaded as soon as it is chosen (same path as photo answers), so
// the schema only ever carries the lightweight "photo:<key>" reference; saving
// the form persists that reference.
const FormHeaderEditor = ({ header, updateHeader }) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const bodyId = useId();
  const summary = headerSummary(header);

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      if (typeof dataUrl !== 'string') throw new Error('Failed to encode image');
      if (dataUrl.length > MAX_PHOTO_BYTES) {
        setError('That image is too large even after compression. Try a smaller one.');
      } else {
        updateHeader({ logo: await uploadPhoto(dataUrl) });
      }
    } catch (err) {
      console.error('Header logo upload failed:', err);
      setError('Could not upload that image. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-6">
      {/* The heading holds the toggle rather than sitting inside it: a button
          may only contain phrasing content. */}
      <h2>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="w-full flex items-center gap-3 text-left bg-transparent hover:bg-transparent p-0 rounded text-xl font-semibold text-white select-none"
        >
          <ChevronIcon expanded={open} />
          <span className="flex-1 min-w-0">Report header</span>
          <span
            className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${
              hasHeaderContent(header)
                ? 'bg-purple-500/15 text-purple-200 border-purple-500/30'
                : 'bg-gray-700/50 text-gray-400 border-gray-600'
            }`}
          >
            {summary}
          </span>
        </button>
      </h2>
      <p className="text-sm text-gray-400 mt-1 pl-7">
        Optional. Appears at the top of this form and of every submission and export made from it.
      </p>

      <div id={bodyId} hidden={!open} className="mt-4">
        <label htmlFor="fb-header-text" className={LABEL}>Header text</label>
        <textarea
          id="fb-header-text"
          value={header.text}
          onChange={(e) => updateHeader({ text: e.target.value })}
          maxLength={MAX_HEADER_TEXT}
          rows={3}
          placeholder="e.g. Black Magic Inspections — 123 Main St, Springfield — (555) 010-0100"
          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
        />
        <div className="mt-1 text-xs text-gray-500">
          {header.text.length}/{MAX_HEADER_TEXT} characters
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div>
            <div className={LABEL}>Logo</div>
            <input
              ref={inputRef}
              id="fb-header-logo"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="sr-only"
            />
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="fb-header-logo" className={`${ACTION_BTN} cursor-pointer`}>
                {header.logo ? 'Replace logo' : 'Upload logo'}
              </label>
              {header.logo && (
                <button
                  type="button"
                  onClick={() => updateHeader({ logo: '' })}
                  className={DANGER_ICON_BTN}
                  title="Remove logo"
                  aria-label="Remove logo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              {uploading && <span className="text-sm text-gray-300">Uploading…</span>}
            </div>
            {error && <div className="mt-2 text-sm text-red-400">{error}</div>}
          </div>

          <div>
            <div className={LABEL}>Logo position</div>
            <div className="inline-flex rounded-lg border border-gray-600 overflow-hidden">
              {HEADER_POSITIONS.map((position) => (
                <button
                  key={position.value}
                  type="button"
                  onClick={() => updateHeader({ logoPosition: position.value })}
                  aria-pressed={header.logoPosition === position.value}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    header.logoPosition === position.value
                      ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 hover:text-white'
                  }`}
                >
                  {position.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {hasHeaderContent(header) && (
          <div className="mt-5">
            <div className={LABEL}>Preview</div>
            <FormHeaderBanner
              header={header}
              className="rounded-lg border border-gray-600 bg-gray-700/30 p-4"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FormHeaderEditor;
